import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select } from '../../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function City() {
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCity, setNewCity] = useState({ name: '', country_id: '', state_id: '' });
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (newCity.country_id) {
      setFilteredStates(states.filter(s => s.country_id === newCity.country_id));
    } else {
      setFilteredStates([]);
    }
    setNewCity(prev => ({ ...prev, state_id: '' }));
  }, [newCity.country_id, states]);

  const fetchData = async () => {
    try {
      const token = Cookies.get('admin_token');
      const [citiesRes, countriesRes, statesRes] = await Promise.all([
        axios.get(`${API}/master/cities`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${API}/master/countries`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${API}/master/states`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      setCities(citiesRes.data.data || []);
      setCountries(countriesRes.data.data || []);
      setStates(statesRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newCity.name.trim() || !newCity.country_id || !newCity.state_id) return;

    setAdding(true);
    try {
      const token = Cookies.get('admin_token');
      await axios.post(`${API}/master/cities`, newCity, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('City added successfully');
      setNewCity({ name: '', country_id: '', state_id: '' });
      setOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add city');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this city?')) return;

    try {
      const token = Cookies.get('admin_token');
      await axios.delete(`${API}/master/cities/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('City deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete city');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading mb-2">City</h1>
          <p className="text-muted-foreground">Manage cities in the system</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="editorial">
              <Plus className="h-4 w-4 mr-2" /> Add City
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New City</DialogTitle>
              <DialogDescription>Select country, state and enter city name</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Country *</Label>
                    <Select
                      value={newCity.country_id}
                      onChange={(e) => setNewCity({...newCity, country_id: e.target.value})}
                      disabled={adding}
                      required
                    >
                      <option value="">Select country</option>
                      {countries.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>State *</Label>
                    <Select
                      value={newCity.state_id}
                      onChange={(e) => setNewCity({...newCity, state_id: e.target.value})}
                      disabled={adding || !newCity.country_id}
                      required
                    >
                      <option value="">Select state</option>
                      {filteredStates.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>City Name *</Label>
                  <Input
                    value={newCity.name}
                    onChange={(e) => setNewCity({...newCity, name: e.target.value})}
                    placeholder="Enter city name"
                    disabled={adding}
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={adding}>
                  Cancel
                </Button>
                <Button type="submit" disabled={adding || !newCity.name.trim() || !newCity.country_id || !newCity.state_id}>
                  {adding ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</>
                  ) : (
                    'Add City'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Cities List */}
      <Card>
        <CardHeader>
          <CardTitle>All Cities ({cities.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : cities.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No cities added yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cities.map((city) => (
                <div
                  key={city.id}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div>
                    <p className="font-medium">{city.name}</p>
                    <p className="text-sm text-muted-foreground">{city.state_name}, {city.country_name}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(city.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
